<script lang="ts">
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { CollectionItem } from './CollectionItem';
	import { ARTIST_PROJECTS_LIMIT } from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { transformCataloguePrimaryArtworks } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformCataloguePrimaryArtworks/index.js';
	import { PrimaryRoutes } from '#lib/websites/primary/constants/routes.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-primary`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let catalogue = $derived(page.data.catalogue as ArtistsPageData['catalogue']);

	// One card per primary collection; a collection is never broken down into
	// its individual artworks.
	let items = $derived(
		transformCataloguePrimaryArtworks(catalogue?.primaryCollections)
	);
	let visibleCount = $state(
		getArtistSectionCount('primary') ?? ARTIST_PROJECTS_LIMIT
	);
	let visibleItems = $derived(items.slice(0, visibleCount));
	let canViewMore = $derived(visibleCount < items.length);

	// Same-route navigations reuse this component instance, so re-read the new
	// history entry's persisted count (or fall back to the default) whenever the
	// artist — or, on back/forward, the entry's state — changes.
	$effect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		artist.id;
		visibleCount = getArtistSectionCount('primary') ?? ARTIST_PROJECTS_LIMIT;
	});

	const showMore = () => {
		visibleCount += ARTIST_PROJECTS_LIMIT;
		setArtistSectionCount('primary', visibleCount);
	};
	// The legacy filter drawer expects string-array values to remain encoded
	// after URLSearchParams performs its first decode, hence the double encoding.
	let viewAllHref = $derived(
		`${PrimaryRoutes.Releases}?1=${encodeURIComponent(
			encodeURIComponent(artist?.name || '')
		)}&1_type=3`
	);
</script>

{#if items.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="PRIMARY"
			href={viewAllHref}
			dataCy={dataCyPrefix}
			label="View all primary"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each visibleItems as item (item.href)}
				<CollectionItem {dataCy} {item} />
			{/each}
		</div>
		{#if canViewMore}
			<Button
				dataCy={`${dataCyPrefix}-view-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				onclick={showMore}>Show more</Button
			>
		{/if}
	</div>
{/if}
